import React, {createRef, useState, useEffect, useRef, Fragment} from 'react';
import {useQueryClient} from "react-query";
import moment from "moment";
import __ from "lodash";
import { Grid, Icon, IconButton, makeStyles} from "@material-ui/core";
import { v4 as uuidv4 } from 'uuid';

import {MaskedText, DatePicker, FormDialog, Text, FormSelect, FormCheckbox} from "../../../../controls";
import TripLegEditor from "./TripLegEditor";
import BuildPrimaryRouteFromLegs from '../../utils/BuildPrimaryRouteFromLegs';
import BuildOnwardRoutesFromLegs from '../../utils/BuildOnwardRoutesFromLegs';
import BuildTruckRoutesFromLegs from '../../utils/BuildTruckRoutesFromLegs';
import Validator from "../../validators/EditTripValidator";
import useTripDepartmentUsers from "../../../../services/auth/useTripDepartmentUsers";
import AddCostingDialog from '../../../Costings/components/AddCostingDialog';
import CostingsStyles from '../../../Costings/styles/CostingsStyles';
import { useAddCosting, useUpdateCosting } from '../../../../services/costings';
import { useTripClients } from '../../../../services/trips';
import { fetch as fetchCostingFuelRate } from '../../../../services/costings/useCostingFuelRate';
import { getCostingByUcn, getCostingDefaultValues } from '../../../Costings/includes/costingDialogFunctions';
import EditCostingDialog from '../../../Costings/components/EditCostingDialog';
import ViewCostingDialog from '../../../Costings/components/ViewCostingDialog';
import ContextMenu from '../../../../controls/ContextMenu';
import { MonetizationOn } from '@material-ui/icons';
import { Preview } from '@mui/icons-material';

const EditTripDialog = (_props) => {
  const props = {
    canEdit: false,
    ..._props,
  }

  const emptyCosting = {
    costingDate: moment.utc().toISOString(),
    routeWinds: process.env.REACT_APP_DEFAULT_WINDCODE || 'GRIB-UKO-WX',
    routeWindsMonth: "",
    legs:[],
    cargo: [],
    services: [],
    weightUom: "KG",
    contingencyFuelCostRate:0,
    contingencyBlockHoursRate:0,
  }

  const SystemData = props.systemData;
  const apiSitePrefix = props.apiSitePrefix;
  const extraKeys = props.extraKeys;
  const handleUpdate = props.handleUpdate;
  const formatChars = props.formatChars;
  const classes = props.classes;
  const tripOwnerDepartment = props.ownerDepartment;

  const requestRef = createRef(0);
  const eventBus = SystemData.eventBus;

  const costingClasses = makeStyles(CostingsStyles)();

  const [dialogStates, setDialogStates] = useState({
    showDialog: false,
    originalRecord: {},
    record: {},
    title: 'Edit Trip'
  });

  const [costingDefaultValues, setCostingDefaultValues] = useState({});

  const [legsChanged, setLegsChanged] = useState(false);

  useEffect(()=> {
    const open = async (record) => {
      setDialogStates({...dialogStates, showDialog:true, record, originalRecord: __.cloneDeep(record)});
      setLegsChanged(false);
    }

    eventBus.bind("EditTripDialog.open", open);

    return () => {
      eventBus.unbind("EditTripDialog.open", open);
    }
  }, [dialogStates, eventBus, extraKeys]);

  if (!costingDefaultValues || __.isEmpty(costingDefaultValues)) {
    getCostingDefaultValues(apiSitePrefix, SystemData, setCostingDefaultValues);
  }

  const tomorrow = moment.utc().add(1, 'day');
  const minDate = moment.utc().startOf('month').subtract(7, 'days');
  const maxDate = moment(minDate).add(3, 'year');
  const initialDate = tomorrow.toISOString();
  const dateFormat = "DD/MM/YYYY";

  const queryClient = useQueryClient();
  const departmentUsers = useTripDepartmentUsers({queryClient, extraKeys: [tripOwnerDepartment]});
  const tripClients = useTripClients({queryClient, extraKeys: [dialogStates?.record?.tripUuid||'UNKNOWN']});

  const handleAddSector = () => {
    if (eventBus) {
      eventBus.trigger('Trip.AddSector');
    }
  }

  const handleLegsChanged = (changes, props) => {
    const {record, recordChange} = props;
    if (changes && changes.record) {
      const legs = __.sortBy(changes.record.legs, ['contractedDepartureTimeUTC']);
      record['legs'] = legs;
      recordChange({...record, legs});
      setLegsChanged(true);
      if (changes.resolve) {
        changes.resolve();
      }
    }
  }

  const handleAddCosting = useAddCosting();
  const handleUpdateCosting = useUpdateCosting();

  const viewCosting = async (record) => {
    if (record.uniqueCostingNo) {
      const costingRecord = await getCostingByUcn(record.uniqueCostingNo, apiSitePrefix, SystemData);
      if (costingRecord) {
        eventBus.trigger("ViewCostingDialog.open", costingRecord);
      }
    }
  }

  const editCosting = async (record) => {
    if (record.uniqueCostingNo) {
      const costingRecord = await getCostingByUcn(record.uniqueCostingNo, apiSitePrefix, SystemData);
      if (costingRecord) {
        eventBus.trigger("EditCostingDialog.open", costingRecord);
      }
    }
  }

  const checkForChanges = (record) => {
    const {originalRecord} = dialogStates;
    if (legsChanged) return true;

    const fieldsToCompare = ['tripDate', 'commercialOwnerId', 'uniqueCostingNo'];
    for (let i = 0; i < fieldsToCompare.length; i++) {
      const fieldName = fieldsToCompare[i];
      if (record[fieldName] !== originalRecord[fieldName]) return true;
    }

    return false;
  }

  const generateCosting = async (record) => {
    const recordChanged = checkForChanges(record);

    if (recordChanged) {
      SystemData.alert("error", "Please save all changes before Generating a Costing");
      return;
    }

    const cargo = [];
    const legs = [];
    let costingDate = record?.tripDate || moment.utc().toISOString();

    let costingAircraftReg = null;

    if (tripClients && Array.isArray(tripClients.data)) {
      for (let i = 0; i < tripClients.data.length; i++) {
        const costingCargoUuid = uuidv4();
        const {origin: originAirportCode, destination: destinationAirportCode, companyName, companyUuid, allotmentKg: payloadLoose} = tripClients.data[i];
        const consignment = {costingCargoUuid, originAirportCode, destinationAirportCode, companyName, companyUuid, payloadLoose, payloadUnitised: 0, bupCount: 0, includeHandling: true, cargoSecure: true};

        cargo.push(consignment);
      }
    }

    if (record && Array.isArray(record.legs)) {
      for (let i = 0; i < record.legs.length; i++) {
        const costingSectorUuid = uuidv4();
        const {originAirportCode, destinationAirportCode, alternateAirportCode, transportType, legType, aircraftId: aircraftReg } = record.legs[i];

        const fuelRecord = await fetchCostingFuelRate({queryClient, extraKeys:[originAirportCode, costingDate], useCache: true});
        let originFuelRecord = {success: false, error: fuelRecord.error};
        let fuelPrice = null;
        if (fuelRecord && fuelRecord.success && fuelRecord.rateRecord) {
          const {rate} = fuelRecord.rateRecord;
          fuelPrice = rate;
          originFuelRecord = {...fuelRecord.rateRecord, success: true};
        }

        const leg = {
          costingSectorUuid,
          orderSequence: i,
          originAirportCode,
          destinationAirportCode,
          alternateAirportCode,
          transportType,
          legType,
          aircraftReg,
          flightJetPlanUuid: null,
          fuelPolicyType: "",
          planData: null,
          fuelPrice,
          originFuelRecord,
          revenueCalculationMethod: "COST PLUS" ,
          revenueCalculationAmount: 0
        };
        if (aircraftReg) costingAircraftReg = aircraftReg;
        legs.push(leg);
      }
    }

    if (!__.isNil(costingDefaultValues?.contingencyFuelCostRate)) emptyCosting['contingencyFuelCostRate'] = costingDefaultValues.contingencyFuelCostRate;
    if (!__.isNil(costingDefaultValues?.contingencyBlockHoursRate)) emptyCosting['contingencyBlockHoursRate'] = costingDefaultValues.contingencyBlockHoursRate;

    const newCostingRecord = {
      ...emptyCosting,
      // enquiryUuid: record.enquiryUuid,
      tripUuid: record.tripUuid,
      commercialOwnerId: record.commercialOwnerId,
      revenueCalculationMethod: "COST PLUS",
      cargo,
      legs,
      aircraftReg: costingAircraftReg,
      costingDate
    }
    eventBus.trigger('AddCostingDialog.open', newCostingRecord);
  }

  const handleGenerateCosting = (record) => {
    generateCosting(record);
  }

  const userOptions = departmentUsers.data?departmentUsers.data.map((userOption, i) => {
    return {code: userOption.user_id, value: `${userOption.firstName} ${userOption.lastName}`};
  }).sort((u1, u2) => {
    if (u1.value === u2.value) return 0;
    if (u1.value < u2.value) return -1;
    return 1;
  }):null;

  if (dialogStates.showDialog) {
    return (
      <>
        <FormDialog
          formId="editTrip"
          title={dialogStates.title}
          message=""
          open={dialogStates.showDialog}
          record={dialogStates.record}
          dialogOptions={{maxWidth: "lg", fullWidth: true}}
          classes={classes}
          validator={Validator}
          movable={true}
          buttons = {{
            generateCosting: {icon: "monetization_on", key: "Generate Costing", label: "Generate Costing", color: "primary", disabled: (status)=>{
              console.log(status);
              if (status.buttonState && status.buttonState.disabled) return true;
              return status.error;
            }},
            addSector: {icon: "add_circle", key: "Add Sector", label: "Add Sector", color: "primary", disabled: moment(dialogStates.record.tripDate).isBefore(moment().subtract(7,'days'))},
            save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{
              console.log(status);
              if (status.buttonState && status.buttonState.disabled) return true;
              return status.error;
            }},
            cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
          }}
          onChange={(state) => {
            if (state.buttonPressed === "save") {
              const record = state.record;
              handleUpdate.mutate({
                extraKeys,
                record,
                postSuccess: () => {
                  setDialogStates({...dialogStates, showDialog: false, record: null})
                  if (typeof props.onClose === 'function') {props.onClose()}
                },
                postError: (data) => {
                  if (data.errorMessage) {
                    SystemData.alert("Error", `${data.errorMessage}`);
                  } else {
                    SystemData.alert("Error", `An Unexpected Error has occurred.`);
                  }
                }
              });
            } else if (state.buttonPressed === "generateCosting") {
              const record = state.record;
              handleGenerateCosting(record);
            } else if (state.buttonPressed === "addSector") {
              handleAddSector();
            } else {
              setDialogStates({...dialogStates, showDialog: false, record: null});
              if (typeof props.onClose === 'function') {props.onClose()}
            }
          }}

        >
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={2} checkChildren={true}>
            {(record, changeRecord) => (
              <div style={{display: "flex"}}>
                <div style={{display: 'inline-flex', width: "calc(100% - 50px)"}}>
                  {record.canChangeContractedTimes === true?
                  <DatePicker
                    id="tripDate"
                    label="Trip Date"
                    style={{width: "100%"}}
                    minDate={minDate}
                    maxDate={maxDate}
                    defaultValue={initialDate}
                    disableToolbar={true}
                    autoOk={true}
                    format={dateFormat}
                    onChange={(value, props) => {
                      const {record, recordChange} = props;
                      const {tripDate, legs} = record;
                      const newTripDate = moment.utc(value);
                      if (!newTripDate.isValid()) return;
                      const diffDays = newTripDate.diff(tripDate, 'days');
                      for (let i = 0; i < legs.length; i++) {
                        const leg = legs[i];
                        let contractedDepartureTimeUTC = leg.contractedDepartureTimeUTC;
                        let contractedArrivalTimeUTC = leg.contractedArrivalTimeUTC;
                        if (!contractedArrivalTimeUTC) {
                          contractedArrivalTimeUTC = moment(tripDate).startOf('day').toISOString();
                        }
                        if (!contractedDepartureTimeUTC) {
                          contractedDepartureTimeUTC = moment(tripDate).startOf('day').toISOString();
                        }
                        leg.contractedDepartureTimeUTC = moment.utc(contractedDepartureTimeUTC).add(diffDays, 'days').toISOString();
                        leg.contractedArrivalTimeUTC = moment.utc(contractedArrivalTimeUTC).add(diffDays, 'days').toISOString();
                        leg.scheduledDepartureTimeUTC = leg.contractedDepartureTimeUTC;
                        leg.scheduledArrivalTimeUTC = leg.contractedArrivalTimeUTC;
                      }
                      recordChange({tripDate: value, legs });
                    }}
                  /> :
                  <div style={{display: 'inline-flex'}}>
                    <div style={{display: "block"}}>
                      <label htmlFor="primaryRoute" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                        Trip Date
                      </label>
                      <Text
                        style={{width: "100%", marginTop: 2, display: "inline-block"}}
                        defaultValue={moment(record.tripDate).format('DD/MM/YYYY')}
                      />
                    </div>
                  </div>}
                </div>
                <div style={{display: 'inline-flex'}}>
                  <IconButton onClick={() => {
                      changeRecord({canChangeContractedTimes: !(record.canChangeContractedTimes === true)});
                    }}
                    disabled={moment(record.tripDate).isBefore(moment().subtract(7,'days'))}
                  >{record.canChangeContractedTimes === true?<Icon>lock_open</Icon>:<Icon>lock</Icon>}</IconButton>
                </div>
              </div>
            )}
            </Grid>

            <Grid item xs={2} checkChildren={true}>
              {(record, changeRecord) => (
                <div>
                  <label htmlFor="primaryRoute" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                    Trip Number
                  </label>

                  <Text
                    style={{width: "100%", marginTop: 2, display: "inline-block"}}
                    defaultValue={`${record.tripPrefix}${record.tripNo}`}
                  />
                </div>
              )}
            </Grid>
            {props.source !== "FLIGHT PLANNING"
              ?
                <Fragment checkChildren={true}>
                  <Grid item xs={2} checkChildren={true} style={{marginTop: 1}}>
                    {record => (
                        <FormSelect
                          id="commercialOwnerId"
                          label="Account Manager"
                          options={userOptions}
                          readOnly={false}
                          value={record.commercialOwnerId}
                          onChange={(value, props) => {
                            props.onChange(value);
                          }}
                          style={{width: 200}}
                        />
                      )
                    }
                  </Grid>
                  <Grid item xs={2} checkChildren={true}>
                    <MaskedText
                      id="uniqueCostingNo"
                      label="Costing Number"
                      style={{width: "100%"}}
                      mask="UUU-9999999"
                      maskChar={""}
                      alwaysShowMask={false}
                      formatChars={formatChars}
                      forceUpperCase={true}
                      defaultValue=""
                      inputLabelProps={{shrink: true}}
                    />
                  </Grid>
                  <Grid item xs={1} checkChildren={true}>
                    {(record, changeRecord) => {
                      return (
                        <FormCheckbox
                          id="gsaTrip"
                          key="gsaTrip"
                          label="GSA Trip"
                          className={classes.checkbox}
                          checked={record.gsaTrip}
                        />
                      )
                    }}
                  </Grid>
                </Fragment>
              :
                <Grid item xs={2} checkChildren={true}>
                  {(record, changeRecord) => {
                    if (record.uniqueCostingNo) return (
                      <ContextMenu
                        menuItems={[
                          {code: "EDIT", label: "Edit Costing", icon: <MonetizationOn/>, action: ()=>{editCosting(record)}},
                          {code: "VIEW", label: "View Costing", icon: <Preview/>, action: ()=>{viewCosting(record)}},
                        ]}
                      >
                        <Text
                          id="uniqueCostingNo"
                          label="Costing Number"
                          style={{width: "100%"}}
                          mask="UUU-9999999"
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={formatChars}
                          forceUpperCase={true}
                          defaultValue=""
                          inputLabelProps={{shrink: true}}
                          onClick={() => {
                            viewCosting(record);
                          }}
                        />
                      </ContextMenu>
                    )
                    return (
                      <Text
                        id="uniqueCostingNo"
                        label="Costing Number"
                        style={{width: "100%"}}
                        mask="UUU-9999999"
                        maskChar={""}
                        alwaysShowMask={false}
                        formatChars={formatChars}
                        forceUpperCase={true}
                        defaultValue=""
                        inputLabelProps={{shrink: true}}
                      />
                    )
                  }}
                </Grid>
            }
          </Grid>
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={4} checkChildren={true}>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="primaryRoute" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Route
                </label>
              </div>
              <div id="primaryRoute" style={{paddingTop: 6}}>
                {(rowData) => (<BuildPrimaryRouteFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={4} checkChildren={true}>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="onwardRoutes" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Onward Routes
                </label>
              </div>
              <div id="onwardRoutes" style={{paddingTop: 6}}>
                {(rowData) => (<BuildOnwardRoutesFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={4} checkChildren={true}>
            <div className="d-flex flex-row justify-content-between align-items-center">
                <label htmlFor="truckRoutes" className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-animated MuiInputLabel-shrink">
                  Trucking
                </label>
              </div>
              <div id="truckRoutes" style={{paddingTop: 6}}>
                {(rowData) => (<BuildTruckRoutesFromLegs legs={rowData.legs}/>)}
              </div>
            </Grid>
            <Grid item xs={12} checkChildren={true}>
            {(record, changeRecord, changeButtonState) => (
              <TripLegEditor
                tripRecord={record}
                handleUpdate={handleUpdate}
                extraKeys={extraKeys}
                canEdit={!moment(record.tripDate).isBefore(moment().subtract(7,'days'))}
                classes={classes}
                onChange={handleLegsChanged}
                canChangeContractedTimes={record.canChangeContractedTimes}
                changeButtonState={changeButtonState}
              />
            )}
            </Grid>
          </Grid>
          {(rowData, changeRecord, changeButtonState) => {
            return (
              <AddCostingDialog
                SystemData={SystemData}
                extraKeys={extraKeys}
                apiSitePrefix={apiSitePrefix}
                defaultValues={costingDefaultValues}
                classes={costingClasses}
                handleAdd={handleAddCosting}
                source={props.source}
                afterSave={(savedCosting)=>{
                  if (props.source === 'FLIGHT PLANNING') {
                    if (savedCosting?.costingNumber) {
                      changeRecord({uniqueCostingNo: `UCN-${savedCosting.costingNumber}`});
                    }
                    // queryClient.invalidateQueries(['Trips']);
                    // setDialogStates({...dialogStates, showDialog: false, record: null});
                  } else {
                    queryClient.invalidateQueries(['Trips']);
                    setDialogStates({...dialogStates, showDialog: false, record: null});
                  }
                }}
              />
            )
          }}
          {(rowData, changeRecord, changeButtonState) => {
            return (
              <EditCostingDialog
                SystemData={SystemData}
                extraKeys={extraKeys}
                apiSitePrefix={apiSitePrefix}
                defaultValues={costingDefaultValues}
                classes={costingClasses}
                handleUpdate={handleUpdateCosting}
                source={props.source}
                afterSave={(savedCosting)=>{
                  if (props.source === 'FLIGHT PLANNING') {
                    changeRecord({uniqueCostingNo: `UCN-${savedCosting.costingNumber}`});
                    // queryClient.invalidateQueries(['Trips']);
                    // setDialogStates({...dialogStates, showDialog: false, record: null});
                  } else {
                    queryClient.invalidateQueries(['Trips']);
                    setDialogStates({...dialogStates, showDialog: false, record: null});
                  }
                }}
              />
            )
          }}
          {(rowData, changeRecord, changeButtonState) => {
            return (
              <ViewCostingDialog
                SystemData={SystemData}
                extraKeys={extraKeys}
                apiSitePrefix={apiSitePrefix}
                defaultValues={costingDefaultValues}
                classes={costingClasses}
                source={props.source}
              />
            )
          }}
        </FormDialog>
      </>
    )
  }
  return (<></>);
}

export default EditTripDialog;
